import { MutableRefObject } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { IRtcStats, IVideoRoomTokenRequest, IVideoRoomTokenResponse } from 'vcs-common';

import { useVideoRoom } from '../../hooks/useVideoRoom.livekit';
import { setActiveTool } from '../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../components/meet/tools-panel/types';
import { Figure, PlacedFigure } from '../../components/tools/sand-therapy/types';

import { createCloudsRoomHandlers } from './tools/clouds/clouds.roomHandlers';
import { createEmotionsRoomHandlers } from './tools/emotions/emotions.roomHandlers';
import { createMacRoomHandlers } from './tools/mac/mac.roomHandlers';

import type { MeetModerationBridgeApi } from './MeetModerationProvider';
import type { MeetOverlaysBridgeApi } from './MeetOverlaysProvider';
import type { MeetToolsBridgeApi } from './MeetToolsProvider';
import type { SupervisorNotification } from './useMeetSupervisor';

type VideoRoomApi = ReturnType<typeof useVideoRoom> & {
  prepareLocalMedia?: () => Promise<void>;
  audioDevice?: MediaDeviceInfo | null;
  videoDevice?: MediaDeviceInfo | null;
  initialized: boolean | null;
  isScreenSharing: boolean | null;
};

interface UseMeetVideoRoomBridgeArgs {
  roomName: string;
  isPsychologist: boolean;
  isGroupConsultationFlag: boolean;
  dispatch: (action: unknown) => void;
  navigate: NavigateFunction;
  generateRoomToken: (request: IVideoRoomTokenRequest) => Promise<IVideoRoomTokenResponse | undefined>;
  onStatsReport: (report: IRtcStats) => Promise<void>;
  setSupervisorNotificationRef: MutableRefObject<(value: SupervisorNotification | null) => void>;
  setSupervisorSendRef: MutableRefObject<(value: boolean) => void>;
  refetchRecordingStatusRef: MutableRefObject<() => void>;
  emitSessionEndedRef: MutableRefObject<() => void>;
  setIsCoOrganizerRef: MutableRefObject<(value: boolean) => void>;
  onMutedByHostRef: MutableRefObject<() => void>;
  onVideoStoppedByHostRef: MutableRefObject<() => void>;
  toolsApiRef: MutableRefObject<MeetToolsBridgeApi | null>;
  overlaysApiRef: MutableRefObject<MeetOverlaysBridgeApi | null>;
  moderationApiRef: MutableRefObject<MeetModerationBridgeApi | null>;
  onChatMessage?: (message: {
    id: string;
    kind: 'text' | 'image';
    text: string;
    sentAt: string;
    senderId: string;
    senderName?: string;
    image?: {
      dataUrl: string;
      mimeType: string;
      width: number;
      height: number;
    };
  }) => void;
  onColorCodeOpen?: () => void;
  onColorCodeClose?: () => void;
  onColorCodeResult?: (payload: { from: string; selectedColors: string[] }) => void;
  onEmotionsOpen?: () => void;
  onEmotionsClose?: () => void;
  onEmotionsSelect?: (payload: { from: string; result: { category: string[]; emo: string[] } }) => void;
  onWheelOpen?: (payload: {
    values: number[];
    sectorNames?: string[];
    isRealtimeVisible?: boolean;
    isSelectionUnlocked?: boolean;
  }) => void;
  onWheelChange?: (payload: { from: string; values: number[] }) => void;
  onWheelResult?: (payload: { from: string; values: number[] }) => void;
  onWheelClose?: () => void;
  onDpdgOpen?: (payload: { initialSync?: Record<string, unknown> }) => void;
  onDpdgClose?: () => void;
  onDpdgMessage?: (action: string, data: Record<string, unknown>) => void;
  onDpdgVideoControl?: (payload: { type: string; time?: number }) => void;
}

export function useMeetVideoRoomBridge({
  roomName,
  isPsychologist,
  isGroupConsultationFlag,
  dispatch,
  navigate,
  generateRoomToken,
  onStatsReport,
  setSupervisorNotificationRef,
  setSupervisorSendRef,
  refetchRecordingStatusRef,
  emitSessionEndedRef,
  setIsCoOrganizerRef,
  onMutedByHostRef,
  onVideoStoppedByHostRef,
  toolsApiRef,
  overlaysApiRef,
  moderationApiRef,
  onChatMessage,
  onColorCodeOpen,
  onColorCodeClose,
  onColorCodeResult,
  onEmotionsOpen,
  onEmotionsClose,
  onEmotionsSelect,
  onWheelOpen,
  onWheelChange,
  onWheelResult,
  onWheelClose,
  onDpdgOpen,
  onDpdgClose,
  onDpdgMessage,
  onDpdgVideoControl,
}: UseMeetVideoRoomBridgeArgs): VideoRoomApi {
  const cloudsHandlers = createCloudsRoomHandlers({
    isPsychologist,
    dispatch,
    overlaysApiRef,
    toolsApiRef,
  });

  const macHandlers = createMacRoomHandlers({
    isPsychologist,
    isGroupConsultationFlag,
    dispatch,
    overlaysApiRef,
  });

  const emotionsHandlers = createEmotionsRoomHandlers({
    isPsychologist,
    isGroupConsultationFlag,
    dispatch,
    overlaysApiRef,
    onEmotionsOpen,
    onEmotionsClose,
    onClientEmotionsSelection: ({ from, selection }) => {
      onEmotionsSelect?.({ from, result: selection });
    },
  });

  return useVideoRoom(
    roomName,
    generateRoomToken,
    onStatsReport,
    (accepted: boolean) => {
      setSupervisorNotificationRef.current(accepted ? { type: 'accepted' } : { type: 'rejected' });
    },
    (sent: boolean | null) => {
      setSupervisorSendRef.current(Boolean(sent));
    },
    () => {
      refetchRecordingStatusRef.current();
    },
    cloudsHandlers.onCloudsOpen,
    cloudsHandlers.onCloudsClose,
    cloudsHandlers.onCloudsDrawLine,
    cloudsHandlers.onCloudsClearDrawing,
    cloudsHandlers.onCloudsSelectImage,
    cloudsHandlers.onCloudsClientFinished,
    cloudsHandlers.onCloudsUndo,
    undefined,
    macHandlers.onMacCardsOpen,
    macHandlers.onClientMacSelection,
    macHandlers.onMacCardsClose,
    emotionsHandlers.onEmotionsOpen,
    emotionsHandlers.onClientEmotionsSelection,
    emotionsHandlers.onEmotionsClose,
    (payload: { identity: string; displayName?: string }) => {
      moderationApiRef.current?.onGroupConnectionRequest(payload);
    },
    isGroupConsultationFlag && !isPsychologist
      ? () => {
          moderationApiRef.current?.handleConnectionAllowed();
        }
      : undefined,
    !isPsychologist
      ? () => {
          onMutedByHostRef.current();
        }
      : undefined,
    !isPsychologist
      ? () => {
          onVideoStoppedByHostRef.current();
        }
      : undefined,
    !isPsychologist && isGroupConsultationFlag
      ? () => {
          emitSessionEndedRef.current();
          navigate(`/end-call/${roomName}`);
        }
      : undefined,
    isGroupConsultationFlag && !isPsychologist
      ? () => {
          setIsCoOrganizerRef.current(true);
        }
      : undefined,
    isGroupConsultationFlag && isPsychologist
      ? (identity: string) => {
          moderationApiRef.current?.handleParticipantLeft(identity);
        }
      : undefined,
    macHandlers.onMacSharedView,
    emotionsHandlers.onEmotionsSharedView,
    () => {
      if (!isPsychologist) {
        dispatch(setActiveTool(PsyToolsId.SAND_THERAPY));
      }
      toolsApiRef.current?.setSandPlacedFigures([]);
      toolsApiRef.current?.setSandSelectedFigures([]);
      toolsApiRef.current?.setSandColor('#fef3c7');
      toolsApiRef.current?.setSandClientReady(false);
    },
    () => {
      if (!isPsychologist) {
        dispatch(setActiveTool(0));
      }
      toolsApiRef.current?.setSandPlacedFigures([]);
      toolsApiRef.current?.setSandSelectedFigures([]);
      toolsApiRef.current?.setSandClientReady(false);
    },
    (figure) => {
      toolsApiRef.current?.setSandPlacedFigures((prev) => [...prev, figure as PlacedFigure]);
    },
    (id, x, y) => {
      toolsApiRef.current?.setSandPlacedFigures((prev) => prev.map((f) => (f.id === id ? { ...f, x, y } : f)));
    },
    (id) => {
      toolsApiRef.current?.setSandPlacedFigures((prev) =>
        prev.map((f) => (f.id === id ? { ...f, rotation: (f.rotation + 45) % 360 } : f)),
      );
    },
    (id, scale) => {
      toolsApiRef.current?.setSandPlacedFigures((prev) => prev.map((f) => (f.id === id ? { ...f, scale } : f)));
    },
    (id) => {
      toolsApiRef.current?.setSandPlacedFigures((prev) => prev.filter((f) => f.id !== id));
    },
    (color) => {
      toolsApiRef.current?.setSandColor(color);
    },
    () => {
      toolsApiRef.current?.setSandClientReady(true);
    },
    (figures) => {
      toolsApiRef.current?.setSandSelectedFigures(figures as Figure[]);
    },
    undefined,
    onChatMessage,
    onColorCodeOpen,
    onColorCodeClose,
    onColorCodeResult,
    onWheelOpen,
    onWheelChange,
    onWheelResult,
    onWheelClose,
    onDpdgOpen,
    onDpdgClose,
    onDpdgMessage,
    onDpdgVideoControl,
    { isGroupConsultation: isGroupConsultationFlag },
  ) as VideoRoomApi;
}
